import { ref } from 'vue'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { usePlatform } from '#imports'

export function useFraudCheck() {
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string | boolean> = ref(false)
    
    const platformStore = usePlatform()
    const { session } = storeToRefs(platformStore)

    async function performFraudCheck(orderId?: string, paymentMethodId?: string): Promise<{ success: boolean; error?: string; blockedPaymentMethod?: string }> {
        try {
            loading.value = true
            error.value = false
            
            console.log('Starting fraud check API call...')
            const { request: __request } = await import('@hubblecommerce/hubble/platforms/shopware/request')
            const { OpenAPI } = await import('@hubblecommerce/hubble/platforms/shopware/api-client')
            
            const requestBody: any = {}
            if (orderId) requestBody.orderId = orderId
            if (paymentMethodId) requestBody.paymentMethodId = paymentMethodId
            
            // If no orderId provided (normal checkout), use session-based check
            if (!orderId && session?.value?.paymentMethod?.id) {
                requestBody.paymentMethodId = session.value.paymentMethod.id
            }

            const crifResponse = await __request(OpenAPI, {
                method: 'POST',
                url: '/_action/crifbuergel/check',
                body: requestBody,
            })

            // @ts-ignore
            if (!crifResponse.success) {
                console.log('Fraud check failed - success=false')
                loading.value = false
                // @ts-ignore
                const errorMessage = crifResponse.error || 'Payment verification failed'
                error.value = errorMessage
                return { success: false, error: errorMessage }
            }

            // @ts-ignore
            if (crifResponse.success && !crifResponse?.paymentMethodAllowed) {
                loading.value = false
                const errorMessage = 'Diese Zahlungsart ist aktuell für den Warenkorb nicht verfügbar, bitte wählen Sie eine andere Zahlungsart aus.'
                error.value = errorMessage
                
                return { 
                    success: false, 
                    error: errorMessage,
                    blockedPaymentMethod: session?.value?.paymentMethod?.name || 'Rechnung'
                }
            }

            loading.value = false
            return { success: true }
            
        } catch (e) {
            loading.value = false
            
            // Check if error is 404 (plugin not active)
            if (e && typeof e === 'object' && 'status' in e && e.status === 404) {
                console.log('Fraud check plugin not active (404) - allowing order to proceed')
                return { success: true }
            }
            
            // Check for response status in different error formats
            if (e && typeof e === 'object') {
                if (('response' in e && e.response && typeof e.response === 'object' && 'status' in e.response && e.response.status === 404) ||
                    ('statusCode' in e && e.statusCode === 404)) {
                    console.log('Fraud check plugin not active (404) - allowing order to proceed')
                    return { success: true }
                }
            }
            
            console.log('Fraud check failed with error:', e)
            const errorMessage = 'Payment verification failed'
            error.value = errorMessage
            return { success: false, error: errorMessage }
        }
    }

    function shouldPerformFraudCheck(): boolean {
        const paymentCode = session?.value?.paymentMethod?.code
        const paymentName = session?.value?.paymentMethod?.name?.toLowerCase()

        return !!(paymentCode === 'invoice_payment' ||
                paymentCode === 'invoice' ||
                paymentName?.includes('rechnung') ||
                paymentName?.includes('invoice'))
    }

    function shouldSkipFraudCheck(customer: any): boolean {
        // Check if customer has the exclude flag set to true
        if (customer?.crifBuergelExcludeFromCheck === true) {
            console.log('Customer excluded from CRIF Bürgel check')
            return true
        }
        return false
    }

    return {
        loading,
        error,
        performFraudCheck,
        shouldPerformFraudCheck,
        shouldSkipFraudCheck
    }
}